/**
 * Bookings — dining & events admin reports for the catering teams.
 *
 * Five report views, all served by /api/dashboards/bookings/reports?report=…
 * over a From/To date window. Not customer-facing, so no fancy templates —
 * just a wide, searchable, scrollable data grid per report + a Print → PDF.
 *
 *   Bookings        — per-item audit (expected vs ordered, variance, allergens)
 *   Customers       — one row per booking (who's seated where)
 *   Detail          — every item per booking + report totals
 *   Menu Exceptions — only the over/short bookings
 *   Summary         — per-service / carriage / item prep totals
 *
 * Search filters the loaded rows client-side (instant); narrowing the date
 * range is what bounds how much is fetched.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "./bookings.css";
import { MasterView } from "./MasterView";
import { AuditView } from "./AuditView";
import { downloadBookingsXlsx } from "./bookingsXlsx";
import { OrientationToggle, type PdfOrientation } from "./orientation";
import { cleanNote, cmpValues, money, prettyDate, timeLabel, type Row } from "./bookingsShared";
import { DEFAULT_SORT, SortReportControls, compareRows, type SortSpec } from "./bookingsSort";
import { downloadCarriageReportPdf } from "./bookingsCarriagePdf";
import { VariantSelect, variantLabel, variantNote, variantParam, type CountVariant, type DedupInfo } from "./bookingsVariant";

// ---------------------------------------------------------------------------
// Report definitions — column layout per report.
// ---------------------------------------------------------------------------
type ReportKey = "bookings" | "customers" | "detail" | "menu-exceptions" | "summary" | "travel-treat" | "fish-chips" | "master" | "audit";

interface ColDef {
  key: string;
  label: string;
  align?: "right" | "center";
  fmt?: (v: unknown) => string;
  /** Fixed column width (mm) in the generated LANDSCAPE PDF — forces the cell to wrap. */
  pdfWidth?: number;
  /** Fixed column width (mm) in the narrower PORTRAIT PDF (falls back to auto). */
  pdfWidthP?: number;
  /** Extra class on the on-screen th/td (e.g. the narrow, wrapping Seats column). */
  cellClass?: string;
}

interface ReportDef {
  key: ReportKey;
  label: string;
  blurb: string;
  columns: ColDef[];
  hasTotals?: boolean;
}

/** A Daily-Bookings "overs and unders" line — the diner ordered more / fewer
 *  covers than expected (Notes = "Over or Short by: N"). */
function isOverShort(row: Row): boolean {
  const n = row["Notes"];
  return typeof n === "string" && n.trim().toLowerCase().startsWith("over or short");
}

function prettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

const COL = {
  day: { key: "Day", label: "Day" },
  date: { key: "JourneyDate", label: "Date", fmt: prettyDate, pdfWidthP: 13 },
  timeA: { key: "Time", label: "Time", pdfWidthP: 14 },
  timeD: { key: "JourneyTime", label: "Time", pdfWidthP: 14 },
  journey: { key: "Journey", label: "Journey" },
  carriage: { key: "Carriage", label: "Carriage" },
  seats: { key: "Seats", label: "Seats", pdfWidth: 26, pdfWidthP: 12, cellClass: "bookings-col-seats" },
  booking: { key: "Booking", label: "Booking", align: "right" as const },
  acc: { key: "AccName", label: "Account" },
  tel: { key: "TelephoneNo", label: "Telephone", pdfWidthP: 15 },
  item: { key: "Item", label: "Item", pdfWidthP: 32 },
  price: { key: "Price", label: "Price", align: "right" as const, fmt: money },
  expected: { key: "Expected", label: "Expected", align: "right" as const },
  ordered: { key: "Ordered", label: "Ordered", align: "right" as const },
  variance: { key: "Variance", label: "Variance", align: "right" as const },
  overshort: { key: "OverOrShort", label: "Over / Short" },
  allergens: { key: "AllergensTicked", label: "Allergens" },
  notes: { key: "Notes", label: "Notes", pdfWidth: 55, pdfWidthP: 42, fmt: cleanNote },
  courses: { key: "Courses", label: "Courses", align: "right" as const },
  covers: { key: "Covers", label: "Covers", align: "right" as const },
  custNo: { key: "AccountNo", label: "Cust No", align: "right" as const },
  allergensYN: { key: "Allergens", label: "Allergens", align: "center" as const },
  confirmed: { key: "ConfirmedCovers", label: "Confirmed", align: "right" as const },
} satisfies Record<string, ColDef>;

// Fish & Chips: the service shown by default (the scheduled dining service that
// runs in the Pullman carriages). Its rows carry this exact Service label from
// the refresh; loco specials / supper / lunch default off behind their pills.
const FC_DEFAULT_SERVICE = "Table for Two/Four";

// Fish & Chips: the optional "PLU ticket type" column, shown only when the pill
// is on (default off). Holds the per-booking PLU ticket-type lines from MERAC
// (e.g. "2x Tornado - Fish & Chips, 1x Tornado - Fish & Chips Member").
const FC_PLU_COL: ColDef = { key: "TicketTypes", label: "PLU ticket type", pdfWidth: 60, pdfWidthP: 42 };

const REPORTS: ReportDef[] = [
  {
    key: "bookings",
    label: "Bookings",
    blurb: "Per-item audit — expected vs ordered covers, variance, allergens flagged and notes.",
    columns: [
      COL.day, COL.date, COL.timeA, COL.journey, COL.carriage, COL.seats, COL.booking,
      COL.acc, COL.tel, COL.item, COL.price, COL.expected, COL.ordered, COL.variance,
      COL.overshort, COL.allergens, COL.notes,
    ],
  },
  {
    key: "customers",
    label: "Customers",
    blurb: "One row per booking — who is seated where on each dining service.",
    columns: [COL.day, COL.date, COL.timeD, COL.carriage, COL.seats, COL.booking, COL.acc, COL.tel],
  },
  {
    key: "detail",
    label: "Daily Bookings",
    blurb: "Every item ordered per booking, with report-wide covers / courses / price totals.",
    columns: [
      COL.day, COL.date, COL.timeD, COL.carriage, COL.seats, COL.booking, COL.acc, COL.tel,
      COL.item, COL.courses, COL.covers, COL.price, COL.notes,
    ],
    hasTotals: true,
  },
  {
    key: "menu-exceptions",
    label: "Menu Exceptions",
    blurb: "Only the bookings whose ordered covers are over or short of what's expected. Click a row to see the booking detail, check allergens, and confirm how many are actually eating.",
    columns: [
      COL.day, COL.date, COL.timeD, COL.carriage, COL.seats, COL.booking, COL.custNo, COL.acc,
      COL.item, COL.covers, COL.allergensYN, COL.confirmed, COL.notes,
    ],
  },
  {
    key: "summary",
    label: "Data List",
    blurb: "Kitchen prep totals — covers / courses / variance per service, carriage and item.",
    columns: [COL.date, COL.day, COL.timeD, COL.carriage, COL.item, COL.covers, COL.courses, COL.variance],
  },
  {
    key: "travel-treat",
    label: "Travel & Treat",
    blurb: "Covers manifest for Travel & Treats / Festive Treats — party size, table size and pre-ordered drinks & food.",
    columns: [
      { key: "ActualDate", label: "Date", fmt: prettyDate },
      { key: "ActualTime", label: "Time" },
      { key: "ActualDay", label: "Day" },
      { key: "Booking", label: "Booking" },
      { key: "Customer", label: "Customer" },
      { key: "Qty", label: "Qty", align: "right" },
      { key: "TableSize", label: "Table Size" },
      { key: "DrinksAndFood", label: "Drinks & Food" },
      { key: "Notes", label: "Notes", fmt: cleanNote },
    ],
  },
  {
    key: "fish-chips",
    label: "Fish & Chips",
    blurb: "Fish & Chips covers manifest across every service — guest, phone, party size, table size, pre-ordered drinks & food (upsells) and notes. Use the service pills to choose which services to show.",
    columns: [
      { key: "Service", label: "Service" },
      { key: "ActualDate", label: "Date", fmt: prettyDate },
      { key: "ActualTime", label: "Time" },
      { key: "ActualDay", label: "Day" },
      { key: "Booking", label: "Booking" },
      { key: "Customer", label: "Name" },
      { key: "Phone", label: "Phone" },
      { key: "People", label: "People", align: "right" },
      { key: "Tables", label: "Table Size" },
      { key: "Seats", label: "Seats / Table", pdfWidth: 45, pdfWidthP: 22, cellClass: "bookings-col-seats" },
      { key: "Upsells", label: "Upsells", pdfWidth: 55, pdfWidthP: 34 },
      { key: "Notes", label: "Notes", pdfWidth: 45, pdfWidthP: 34, fmt: cleanNote },
    ],
  },
  {
    key: "master",
    label: "Summary",
    blurb: "Menu & seat totals by carriage per service (like the SSRS report), with seat/course reconciliation and click-through to the bookings behind every figure.",
    columns: [],
  },
  {
    key: "audit",
    label: "Audit",
    blurb: "Diner Bookings Information Audit — a clean per-booking Summary (expected vs ordered, variance, allergens, over/short status) plus the full Detail grid.",
    columns: [],
  },
];

// Which columns identify a "service" (a dated departure) for a report — used
// by the "select by service" filter. Every report has a date + time column;
// the audit report also carries a Journey name to label the service.
function serviceKeysFor(def: ReportDef): { dateKey: string; timeKey: string; labelKey?: string } | null {
  const keys = new Set(def.columns.map((c) => c.key));
  if (keys.has("ActualDate")) return { dateKey: "ActualDate", timeKey: "ActualTime" };
  if (keys.has("JourneyTime")) return { dateKey: "JourneyDate", timeKey: "JourneyTime" };
  if (keys.has("Time")) return { dateKey: "JourneyDate", timeKey: "Time", labelKey: "Journey" };
  return null;
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

interface BookingsResponse {
  report: ReportKey;
  from: string;
  to: string;
  rows: Row[];
  truncated: boolean;
  last_refresh?: string | null;
  totals?: { Courses: number; Covers: number; Price: number };
  variant?: CountVariant;
  dedup?: DedupInfo;
}

// Shared live refresh status — the latest dining-events refresh request, polled
// by every open Bookings page so an in-progress refresh (and who started it, and
// its outcome) is visible to all viewers, not just whoever clicked.
interface RefreshStatus {
  in_flight: boolean;
  latest: {
    status: string;
    requested_by: string;
    requested_at: string | null;
    completed_at: string | null;
    message: string | null;
  } | null;
  last_refresh: string | null;
}

/** Turn a UPN/email ("shaun.wells@nymr.co.uk") into a display name ("Shaun Wells"). */
function prettyUser(upn: string | null | undefined): string {
  if (!upn) return "someone";
  const local = upn.split("@")[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ") || upn;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function BookingsDashboard() {
  const [report, setReport] = useState<ReportKey>("bookings");
  const [from, setFrom] = useState<string>(() => addDaysIso(todayIso(), -1));
  const [to, setTo] = useState<string>(() => addDaysIso(todayIso(), 14));
  const [search, setSearch] = useState("");
  const [selectedService, setSelectedService] = useState("");
  // Daily Bookings only: show just the "overs and unders" lines.
  const [overShortOnly, setOverShortOnly] = useState(false);
  // Fish & Chips only: which F&C services (by type) to show. Default: the
  // scheduled "Table for Two/Four" on, the loco specials / supper / lunch off.
  const [fcSelected, setFcSelected] = useState<Set<string>>(() => new Set([FC_DEFAULT_SERVICE]));
  // Fish & Chips only: show the PLU ticket-type column against each line. Off by default.
  const [showPlu, setShowPlu] = useState(false);
  // PDF orientation for this tab's printable outputs (default landscape, as before).
  const [orientation, setOrientation] = useState<PdfOrientation>("landscape");
  // Daily Bookings only: click-to-sort by a column head. Reflected in the grid
  // and the flat "Generate PDF" output.
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(null);
  // Bookings only: the two-level "Sort report" order (primary groups, secondary
  // orders within). One owner for the grid, the Excel export and the printed
  // carriage report, whose headings are these two keys.
  const [sortSpec, setSortSpec] = useState<SortSpec>(DEFAULT_SORT);
  // Counting variant — applies to EVERY sub-view, total and export on the page:
  // "As MERAC reports" (the cached rows as Colin's DN_001 emits them — a booking
  // spread over several carriages is counted once per carriage) or "One count
  // per booking" (the API de-duplicates: api/src/bookingDedup.ts). `dedupInfo`
  // is what the server reports it did, for the note under the dropdown.
  const [variant, setVariant] = useState<CountVariant>("merac");
  const [dedupInfo, setDedupInfo] = useState<DedupInfo | null>(null);

  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<BookingsResponse["totals"]>();
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Menu Exceptions: the booking whose fix-it detail popup is open.
  const [bookingModal, setBookingModal] = useState<{ booking: number; journeyDate: string } | null>(null);

  // Refresh-now enqueues a Sync Hub dining-events refresh (reports app.RefreshRequests
  // → Sync Hub scheduler runs Refresh-DiningEvents). `posting` is this user's own
  // click being sent; `refreshStatus` is the SHARED state polled by every page.
  const [posting, setPosting] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const lastRefreshRef = useRef<string | null>(null);
  useEffect(() => { lastRefreshRef.current = lastRefresh; }, [lastRefresh]);

  const def = REPORTS.find((r) => r.key === report)!;
  // Fish & Chips with the PLU pill on: insert the ticket-type column before "Table Size".
  const activeDef: ReportDef = useMemo(() => {
    if (report !== "fish-chips" || !showPlu) return def;
    const cols = [...def.columns];
    const at = cols.findIndex((c) => c.key === "Tables");
    cols.splice(at >= 0 ? at : cols.length, 0, FC_PLU_COL);
    return { ...def, columns: cols };
  }, [def, report, showPlu]);

  // Fetch whenever the report or date window changes.
  useEffect(() => {
    let cancelled = false;
    if (report === "master" || report === "audit") {
      // Master View / Audit fetch their own data — skip the generic grid fetch.
      setRows([]);
      setTotals(undefined);
      setError(null);
      setLoading(false);
      return;
    }
    if (from > to) {
      setError("'From' must be on or before 'To'.");
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    const url = `/api/dashboards/bookings/reports?report=${encodeURIComponent(report)}&from=${from}&to=${to}&limit=20000${variantParam(variant)}`;
    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<BookingsResponse>;
      })
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows ?? []);
        setTotals(data.totals);
        setTruncated(Boolean(data.truncated));
        setLastRefresh(data.last_refresh ?? null);
        setDedupInfo(data.dedup ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setRows([]);
        setTotals(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [report, from, to, reloadNonce, variant]);

  const svcKeys = useMemo(() => serviceKeysFor(def), [def]);

  // Distinct services in the loaded rows — powers the "select by service" filter.
  const services = useMemo(() => {
    if (!svcKeys) return [];
    const seen = new Map<string, { value: string; label: string }>();
    for (const row of rows) {
      const date = row[svcKeys.dateKey];
      const time = row[svcKeys.timeKey];
      if (date == null || time == null) continue;
      const value = `${String(date)}|${String(time)}`;
      if (seen.has(value)) continue;
      const lbl = svcKeys.labelKey ? row[svcKeys.labelKey] : null;
      const label = `${prettyDate(String(date))} ${String(time)}${lbl ? " · " + String(lbl) : ""}`;
      seen.set(value, { value, label });
    }
    return [...seen.values()].sort((a, b) => a.value.localeCompare(b.value));
  }, [rows, svcKeys]);

  // Fish & Chips: the distinct services (by type) present in the loaded rows —
  // powers the per-service pills. The default service is always offered even if
  // it has no bookings in the window, so its pill is always visible.
  const fcServices = useMemo(() => {
    if (report !== "fish-chips") return [];
    const set = new Set<string>();
    for (const row of rows) {
      const s = row["Service"];
      if (typeof s === "string" && s) set.add(s);
    }
    set.add(FC_DEFAULT_SERVICE);
    return [...set].sort((a, b) =>
      a === FC_DEFAULT_SERVICE ? -1 : b === FC_DEFAULT_SERVICE ? 1 : a.localeCompare(b)
    );
  }, [rows, report]);

  // Client-side search + service filter across the report's visible columns.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const keys = def.columns.map((c) => c.key);
    return rows.filter((row) => {
      if (report === "fish-chips" && !fcSelected.has(String(row["Service"] ?? ""))) return false;
      if (selectedService && svcKeys) {
        if (`${String(row[svcKeys.dateKey])}|${String(row[svcKeys.timeKey])}` !== selectedService) return false;
      }
      if (overShortOnly && !isOverShort(row)) return false;
      if (!q) return true;
      return keys.some((k) => {
        const v = row[k];
        return v != null && String(v).toLowerCase().includes(q);
      });
    });
  }, [rows, search, def, selectedService, svcKeys, overShortOnly, report, fcSelected]);

  // The order the report shows — ONE owner for the grid, the Excel export and
  // the PDFs. Bookings: the two-level "Sort report" (primary groups, secondary
  // orders within). Daily Bookings: the click-to-sort column (if any).
  const displayRows = useMemo(() => {
    if (report === "bookings") return [...filtered].sort(compareRows(sortSpec));
    if (report !== "detail" || !sort) return filtered;
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => cmpValues(a[key], b[key]) * factor);
  }, [filtered, report, sort, sortSpec]);

  const toggleSort = useCallback((key: string) => {
    setSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  }, []);

  const toggleFcService = useCallback((s: string) => {
    setFcSelected((prev) => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);
      else n.add(s);
      return n;
    });
  }, []);

  // Bookings in PORTRAIT prints the 14pt carriage report (grouped by the Sort
  // report keys). Landscape — and every other report — prints the original
  // flat table, in the same sorted order. (Chris, 2026-09-17: the landscape
  // sheet was never to change.)
  const onPrint = useCallback(() => {
    if (report === "bookings" && orientation === "portrait") downloadCarriageReportPdf(displayRows, { from, to }, orientation, sortSpec, variantLabel(variant));
    else downloadBookingsPdf(activeDef, displayRows, { from, to }, orientation, variantLabel(variant));
  }, [report, activeDef, displayRows, from, to, orientation, sortSpec, variant]);

  // Export to Excel — the active report tab's columns and currently-filtered rows.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxError, setXlsxError] = useState<string | null>(null);
  const onExportExcel = useCallback(async () => {
    if (xlsxBusy) return;
    setXlsxBusy(true);
    setXlsxError(null);
    try {
      await downloadBookingsXlsx(activeDef, displayRows, {
        from,
        to,
        search,
        serviceLabel: services.find((s) => s.value === selectedService)?.label ?? null,
        overShortOnly: report === "detail" && overShortOnly,
        countingLabel: variantLabel(variant),
      });
    } catch (err) {
      setXlsxError(err instanceof Error ? err.message : String(err));
    } finally {
      setXlsxBusy(false);
    }
  }, [xlsxBusy, activeDef, displayRows, from, to, search, services, selectedService, report, overShortOnly, variant]);

  // Detail only: a print grouped by booking — each booking is one block with its
  // account / seats / service shown once as a header, then its menu items beneath.
  const onPrintGrouped = useCallback(() => {
    downloadDetailGroupedPdf(displayRows, { from, to }, orientation, variantLabel(variant));
  }, [displayRows, from, to, orientation, variant]);

  // Refresh now: enqueue a dining-events refresh, poll the request, reload on done.
  // Shared status poll — runs on EVERY open page (not just the clicker), so a
  // refresh anyone starts shows for all viewers, and each page auto-reloads its
  // data when it completes.
  const pollStatus = useCallback(async () => {
    try {
      const res = await fetch("/api/dashboards/bookings/refresh", { credentials: "include" });
      if (!res.ok) return;
      const s = (await res.json()) as RefreshStatus;
      setRefreshStatus(s);
      // A refresh finished (the cache's last_refresh advanced past what we're
      // showing) → reload the report so every viewer gets the fresh figures.
      if (s.last_refresh && lastRefreshRef.current && s.last_refresh > lastRefreshRef.current) {
        setReloadNonce((n) => n + 1);
      }
    } catch {
      /* transient — retry next tick */
    }
  }, []);
  useEffect(() => {
    void pollStatus();
    const t = window.setInterval(() => void pollStatus(), 8000);
    return () => window.clearInterval(t);
  }, [pollStatus]);

  // The button is locked out for EVERYONE while a refresh is in flight (shared),
  // and for this user while their own click is being sent.
  const busy = (refreshStatus?.in_flight ?? false) || posting;
  const onRefresh = useCallback(async () => {
    if ((refreshStatus?.in_flight ?? false) || posting) return; // one refresh at a time, for all
    setPosting(true);
    setRefreshMsg(null);
    try {
      const res = await fetch("/api/dashboards/bookings/refresh", {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok && res.status !== 202) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Request failed (${res.status})`);
      }
      // Reflect "in progress" immediately — for this user and, via the shared
      // poll, for everyone else. The heartbeat carries it to completion + reload.
      await pollStatus();
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  }, [refreshStatus, posting, pollStatus]);

  const canPrint = !loading && !error && filtered.length > 0;

  return (
    <main className="app-main bookings">
      <div className="bookings-head">
        <div>
          <p className="bookings-sub">
            Dining &amp; events booking reports for the catering teams. Pick a report,
            set the date range, search and print.
          </p>
        </div>
        <div className="bookings-refresh">
          <button
            type="button"
            className="bookings-refresh-btn"
            onClick={() => void onRefresh()}
            disabled={busy}
            title="Pull the latest bookings from the booking system — usually finishes in under a minute; the report updates for everyone when it completes"
          >
            {busy ? "↻ Refreshing…" : "↻ Refresh now"}
          </button>
          {refreshStatus?.in_flight && (
            <span className="bookings-refresh-msg">
              🔄 Refresh in progress — started by {prettyUser(refreshStatus.latest?.requested_by)}
              {refreshStatus.latest?.requested_at ? ` at ${prettyDateTime(refreshStatus.latest.requested_at)}` : ""}. The report will update automatically for everyone when it finishes.
            </span>
          )}
          {refreshMsg && <span className="bookings-refresh-msg">{refreshMsg}</span>}
          <span className="bookings-refresh-note">
            Data last refreshed at {prettyDateTime(lastRefresh ?? refreshStatus?.last_refresh ?? null)}. A refresh usually finishes in under a minute and updates the report for everyone when it completes.
            {refreshStatus?.latest && !refreshStatus.in_flight && (
              <>
                {" "}Last refresh requested by {prettyUser(refreshStatus.latest.requested_by)}
                {refreshStatus.latest.completed_at ? ` at ${prettyDateTime(refreshStatus.latest.completed_at)}` : ""}
                {refreshStatus.latest.status === "completed"
                  ? " — ✓ successful"
                  : refreshStatus.latest.status === "error"
                  ? ` — ✗ failed${refreshStatus.latest.message ? ` (${refreshStatus.latest.message})` : ""}`
                  : ""}.
              </>
            )}
          </span>
        </div>
      </div>

      {/* Report selector */}
      <div className="bookings-reports" role="tablist" aria-label="Report">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={r.key === report}
            className={"bookings-report-pill" + (r.key === report ? " active" : "")}
            onClick={() => {
              setReport(r.key);
              setSearch("");
              setSelectedService("");
              setFcSelected(new Set([FC_DEFAULT_SERVICE]));
              setSort(null);
            }}
            title={r.blurb}
          >
            {r.label}
          </button>
        ))}
      </div>
      <p className="bookings-report-blurb">{def.blurb}</p>

      {/* Toolbar: dates, search, print */}
      <div className="bookings-toolbar">
        <label className="bookings-field">
          <span>From</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="bookings-field">
          <span>To</span>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
        <VariantSelect value={variant} onChange={(v) => { setVariant(v); setDedupInfo(null); }} />
        {report !== "master" && report !== "audit" && (
          <>
        {services.length > 0 && (
          <label className="bookings-field bookings-service">
            <span>{report === "fish-chips" ? "Departure" : "Service"}</span>
            <select value={selectedService} onChange={(e) => setSelectedService(e.target.value)}>
              <option value="">All services ({services.length})</option>
              {services.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
        )}
        <label className={"bookings-search" + (report === "detail" ? " bookings-search--narrow" : "")}>
          <span className="bookings-search-icon" aria-hidden="true">🔍</span>
          <input
            type="search"
            value={search}
            placeholder="Search name, booking, item…"
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="bookings-search-clear" aria-label="Clear search" onClick={() => setSearch("")}>
              ×
            </button>
          )}
        </label>
        {report === "detail" && (
          <button
            type="button"
            className={"bookings-oversshort-pill" + (overShortOnly ? " active" : "")}
            onClick={() => setOverShortOnly((v) => !v)}
            aria-pressed={overShortOnly}
            title="Show only the overs and unders lines (ordered covers over or short of expected)"
          >
            Overs/Unders
          </button>
        )}
        <span className="bookings-count">
          {loading ? "Loading…" : `${filtered.length.toLocaleString()} row${filtered.length === 1 ? "" : "s"}`}
          {!loading && search && rows.length !== filtered.length ? ` of ${rows.length.toLocaleString()}` : ""}
        </span>
        {xlsxError && <span className="bookings-xlsx-error">Excel export failed: {xlsxError}</span>}
        <div className="bookings-actions">
          {report === "bookings" && <SortReportControls value={sortSpec} onChange={setSortSpec} />}
          <OrientationToggle value={orientation} onChange={setOrientation} />
          <button
            type="button"
            className="bookings-print bookings-print--excel"
            onClick={() => void onExportExcel()}
            disabled={!canPrint || xlsxBusy}
            title={`Export the ${filtered.length.toLocaleString()} row${filtered.length === 1 ? "" : "s"} shown to an Excel workbook`}
          >
            {xlsxBusy ? "Exporting…" : "⤓ Export to Excel"}
          </button>
          <button type="button" className="bookings-print" onClick={onPrint} disabled={!canPrint}>
            🖨 Generate PDF
          </button>
          {report === "detail" && (
            <button
              type="button"
              className="bookings-print"
              onClick={onPrintGrouped}
              disabled={!canPrint}
              title="Print grouped by booking — account, seats and booking shown once per booking, then its menu items and courses"
            >
              🖨 Print (grouped by booking)
            </button>
          )}
        </div>
          </>
        )}
      </div>
      <p className="bookings-variant-note">{variantNote(variant, dedupInfo)}</p>

      {/* Fish & Chips: per-service pills (default: Table for Two/Four on) */}
      {report === "fish-chips" && fcServices.length > 0 && (
        <div className="bookings-fc-services" role="group" aria-label="Fish & Chips services">
          <span className="bookings-fc-services__label">Services:</span>
          {fcServices.map((s) => {
            const on = fcSelected.has(s);
            const count = rows.reduce((n, r) => (String(r["Service"] ?? "") === s ? n + 1 : n), 0);
            return (
              <button
                key={s}
                type="button"
                className={"bookings-fc-pill" + (on ? " active" : "")}
                aria-pressed={on}
                onClick={() => toggleFcService(s)}
                title={on ? `Hide ${s}` : `Show ${s}`}
              >
                {s}{count ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>
      )}

      {/* Fish & Chips: toggle the PLU ticket-type column (default off) */}
      {report === "fish-chips" && (
        <div className="bookings-fc-services" role="group" aria-label="Fish & Chips options">
          <span className="bookings-fc-services__label">Options:</span>
          <button
            type="button"
            className={"bookings-fc-pill" + (showPlu ? " active" : "")}
            aria-pressed={showPlu}
            onClick={() => setShowPlu((v) => !v)}
            title={showPlu ? "Hide the PLU ticket type column" : "Show the PLU ticket type against each line"}
          >
            PLU ticket type
          </button>
        </div>
      )}

      {report === "master" ? (
        <MasterView from={from} to={to} reloadNonce={reloadNonce} onLastRefresh={setLastRefresh} variant={variant} onDedup={setDedupInfo} />
      ) : report === "audit" ? (
        <AuditView from={from} to={to} reloadNonce={reloadNonce} onLastRefresh={setLastRefresh} variant={variant} onDedup={setDedupInfo} />
      ) : (
        <>
      {/* Results grid */}
      <div className="bookings-table-wrap">
        {error ? (
          <div className="bookings-empty bookings-empty--error">Couldn’t load report: {error}</div>
        ) : loading && rows.length === 0 ? (
          <div className="bookings-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bookings-empty">
            {rows.length === 0
              ? "No bookings in this date range. Widen the dates, or the data may not have refreshed yet."
              : "No rows match your search."}
          </div>
        ) : (
          <table className="bookings-table">
            <thead>
              <tr>
                {activeDef.columns.map((c) => {
                  const sortable = report === "detail";
                  const active = sort?.key === c.key;
                  const cls = [
                    c.align ? `ta-${c.align}` : "",
                    c.cellClass ?? "",
                    sortable ? "bookings-th-sort" : "",
                    active ? "active" : "",
                  ].filter(Boolean).join(" ") || undefined;
                  return (
                    <th
                      key={c.key}
                      className={cls}
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : undefined}
                      title={sortable ? `Sort by ${c.label}` : undefined}
                    >
                      {c.label}
                      {sortable && active ? (
                        <span className="bookings-sort-caret" aria-hidden="true">{sort!.dir === "asc" ? " ▲" : " ▼"}</span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, i) => {
                const clickable =
                  report === "menu-exceptions" && row.Booking != null && row.JourneyDate != null;
                const overShort = report === "detail" && isOverShort(row);
                return (
                <tr
                  key={(row.id as number) ?? i}
                  className={[clickable ? "bookings-row-clickable" : "", overShort ? "bookings-row-overshort" : ""].filter(Boolean).join(" ") || undefined}
                  onClick={clickable ? () => setBookingModal({ booking: Number(row.Booking), journeyDate: String(row.JourneyDate) }) : undefined}
                  title={clickable ? "Open booking — check allergens & confirm covers" : undefined}
                >
                  {activeDef.columns.map((c) => {
                    const raw = row[c.key];
                    const text = c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
                    const cls = [c.align ? `ta-${c.align}` : "", c.cellClass ?? ""].filter(Boolean).join(" ") || undefined;
                    return (
                      <td key={c.key} className={cls} title={text}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
            {def.hasTotals && totals && (
              <tfoot>
                <tr>
                  {activeDef.columns.map((c, idx) => {
                    if (idx === 0) return <td key={c.key} className="bookings-totals-label">Report totals</td>;
                    if (c.key === "Courses") return <td key={c.key} className="ta-right">{totals.Courses.toLocaleString()}</td>;
                    if (c.key === "Covers") return <td key={c.key} className="ta-right">{totals.Covers.toLocaleString()}</td>;
                    if (c.key === "Price") return <td key={c.key} className="ta-right">{money(totals.Price)}</td>;
                    return <td key={c.key} />;
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>

      {truncated && (
        <p className="bookings-truncated">
          Showing the first {rows.length.toLocaleString()} rows — narrow the date range to see everything.
        </p>
      )}
        </>
      )}

      {bookingModal && (
        <BookingDetailModal
          booking={bookingModal.booking}
          journeyDate={bookingModal.journeyDate}
          variant={variant}
          onClose={() => setBookingModal(null)}
          onSaved={() => setReloadNonce((n) => n + 1)}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Menu Exceptions fix-it popup — the full detail of one booking (all its items),
// its customer number and allergen flag, the covers the table is taking, and an
// input to confirm how many are actually eating (saved against the booking).
// ---------------------------------------------------------------------------
interface BookingDetailItem {
  Item: string | null; Seq: number | null; Covers: number | null; Courses: number | null;
  Price: number | null; Notes: string | null; Carriage: string | null; Seats: string | null;
  AccName: string | null; Day: string | null; JourneyTime: string | null;
}
interface BookingDetailResponse {
  booking: number; journeyDate: string; account_no: number | null; allergens: string;
  covers_taking: number;
  confirmation: { confirmed_covers: number | null; note: string | null; confirmed_by: string | null; confirmed_at: string | null };
  rows: BookingDetailItem[];
}

function seqLabel(s: number | null): string {
  return s === 0 ? "Seat" : s === 1 ? "Starter" : s === 2 ? "Main" : s === 3 ? "Dessert" : "";
}

function BookingDetailModal({ booking, journeyDate, variant, onClose, onSaved }: {
  booking: number; journeyDate: string; variant: CountVariant; onClose: () => void; onSaved: () => void;
}) {
  const [data, setData] = useState<BookingDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [covers, setCovers] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetch(`/api/dashboards/bookings/booking-detail?booking=${booking}&journeyDate=${encodeURIComponent(journeyDate)}${variantParam(variant)}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error((b as { error?: string }).error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<BookingDetailResponse>;
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setCovers(d.confirmation.confirmed_covers != null ? String(d.confirmation.confirmed_covers) : "");
        setNote(d.confirmation.note ?? "");
      })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [booking, journeyDate, variant]);

  const doSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const cc = covers.trim() === "" ? null : Number(covers);
      if (cc != null && (!Number.isFinite(cc) || cc < 0)) throw new Error("Enter a valid number");
      const res = await fetch(`/api/dashboards/bookings/confirm-covers`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ journey_date: journeyDate, booking, confirmed_covers: cc, note: note.trim() || null }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error((b as { error?: string }).error || `Save failed (${res.status})`);
      }
      setSaveMsg("Saved");
      onSaved();
    } catch (e) {
      setSaveMsg(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const acc = data?.rows[0]?.AccName ?? "";
  const time = data?.rows[0]?.JourneyTime ?? "";
  const timeStr = time.includes("T") ? time.slice(11, 16) : time.replace(/^\d{4}-\d{2}-\d{2}\s*/, "");
  const coversTaking = data?.covers_taking ?? 0;

  return (
    <div className="bookings-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bookings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bookings-modal-head">
          <div>
            <div className="bookings-modal-title">Booking {booking}{acc ? ` · ${acc}` : ""}</div>
            <div className="bookings-modal-sub">
              {prettyDate(journeyDate)}{timeStr ? ` · ${timeStr}` : ""}
            </div>
          </div>
          <button type="button" className="bookings-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="bookings-modal-body">
          {loading ? (
            <div className="bookings-empty">Loading…</div>
          ) : err ? (
            <div className="bookings-empty bookings-empty--error">{err}</div>
          ) : data ? (
            <>
              <div className="bkd-meta">
                <span>Customer no: <strong>{data.account_no ?? "—"}</strong></span>
                <span>
                  Allergens:{" "}
                  <strong className={data.allergens === "Yes" ? "bkd-allergen-yes" : undefined}>
                    {data.allergens}
                  </strong>
                </span>
                <span>Covers the table is taking: <strong>{coversTaking}</strong></span>
              </div>

              <table className="bookings-table bkd-items">
                <thead>
                  <tr><th>Course</th><th>Item</th><th className="ta-right">Covers</th><th className="ta-right" title="Individual servings of this course line required">Servings</th><th className="ta-right">Price</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i}>
                      <td>{seqLabel(r.Seq)}</td>
                      <td>{r.Item ?? ""}</td>
                      <td className="ta-right">{r.Covers ?? ""}</td>
                      <td className="ta-right">{(r.Seq ?? 0) > 0 && r.Courses != null ? r.Courses : ""}</td>
                      <td className="ta-right">{money(r.Price)}</td>
                      <td>{r.Notes ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="bkd-confirm">
                <div className="bkd-confirm-title">Confirm how many are actually eating</div>
                <div className="bkd-confirm-row">
                  <label className="bkd-field">
                    <span>People eating</span>
                    <input
                      type="number"
                      min={0}
                      value={covers}
                      placeholder={String(coversTaking)}
                      onChange={(e) => { setCovers(e.target.value); setSaveMsg(null); }}
                    />
                  </label>
                  <label className="bkd-field bkd-field-grow">
                    <span>Note (optional)</span>
                    <input
                      type="text"
                      value={note}
                      maxLength={400}
                      placeholder="e.g. 2 no-shows; table of 6 but only 4 eating"
                      onChange={(e) => { setNote(e.target.value); setSaveMsg(null); }}
                    />
                  </label>
                  <button type="button" className="bookings-print bkd-save" onClick={() => void doSave()} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
                <div className="bkd-confirm-meta">
                  {saveMsg && <span className="bkd-savemsg">{saveMsg}</span>}
                  {data.confirmation.confirmed_at && (
                    <span>
                      Last confirmed{data.confirmation.confirmed_by ? ` by ${data.confirmation.confirmed_by}` : ""} · {prettyDateTime(data.confirmation.confirmed_at)}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate PDF → build a real landscape PDF (jsPDF + autotable) and download
// it directly. No window.open() — that gets popup-blocked; a direct download
// via doc.save() is how the menu-card generator behaves too.
// ---------------------------------------------------------------------------
function downloadBookingsPdf(def: ReportDef, rows: Row[], range: { from: string; to: string }, orientation: PdfOrientation, countingLabel?: string) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });

  const head = [def.columns.map((c) => c.label)];
  const body = rows.map((row) =>
    def.columns.map((c) => {
      const raw = row[c.key];
      return c.fmt ? c.fmt(raw) : raw == null ? "" : String(raw);
    })
  );
  // Daily Bookings: flag the "overs and unders" lines so they print red.
  const overShortFlags = rows.map((r) => def.key === "detail" && isOverShort(r));

  // Per-column widths (mm) are orientation-specific: the wide landscape sheet
  // uses pdfWidth; the narrow portrait page uses the tighter pdfWidthP so the
  // Seats column stays slim and the long Item / Notes columns keep a real,
  // wrapping width instead of being starved by autotable's content-fit.
  const portrait = orientation === "portrait";
  const columnStyles: Record<string, { halign?: "right" | "center"; cellWidth?: number }> = {};
  def.columns.forEach((c, i) => {
    const s: { halign?: "right" | "center"; cellWidth?: number } = {};
    if (c.align) s.halign = c.align;
    const w = portrait ? c.pdfWidthP : c.pdfWidth;
    if (w) s.cellWidth = w;
    if (s.halign || s.cellWidth) columnStyles[String(i)] = s;
  });

  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });

  // Header band
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(11, 61, 46);
  doc.text(`Dining & Events — ${def.label}`, 10, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(
    `${range.from} to ${range.to}  ·  ${rows.length} row${rows.length === 1 ? "" : "s"}${countingLabel ? `  ·  ${countingLabel}` : ""}  ·  printed ${printedOn}`,
    10,
    17
  );

  autoTable(doc, {
    head,
    body,
    startY: 21,
    margin: { left: 8, right: 8 },
    styles: { fontSize: portrait ? 6.3 : 7, cellPadding: portrait ? 0.9 : 1.2, overflow: "linebreak", valign: "top" },
    headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [250, 248, 242] },
    columnStyles,
    didParseCell: (d) => {
      // "Overs and unders" rows print as a solid RED BLOCK (white bold text).
      if (d.section === "body" && overShortFlags[d.row.index]) {
        d.cell.styles.fillColor = [198, 40, 40];
        d.cell.styles.textColor = [255, 255, 255];
        d.cell.styles.fontStyle = "bold";
      }
    },
  });

  // doc.save() creates a Blob URL + anchor click → direct download, no popup.
  doc.save(`bookings-${def.key}-${range.from}_to_${range.to}.pdf`);
}

// ---------------------------------------------------------------------------
// Daily Bookings (Detail) — a print grouped by booking. Instead of repeating
// the account / seats / booking on every item row (as the on-screen grid does),
// each booking becomes one block: a header carrying account · booking · carriage
// · seats · service (once, surname + carriage in bold), then its menu items +
// courses beneath, and a per-booking subtotal. Each carriage starts on a fresh
// page, and a single booking is never split across two pages. Payment-machine
// noise (Stripe pi_… codes) is stripped from the printed notes.
// ---------------------------------------------------------------------------
function downloadDetailGroupedPdf(rows: Row[], range: { from: string; to: string }, orientation: PdfOrientation, countingLabel?: string) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const landscape = orientation === "landscape";
  const pageW = landscape ? 297 : 210;
  const pageH = landscape ? 210 : 297;
  const M = 8;                        // left margin
  const RIGHT = pageW - 8;            // right edge
  const BOTTOM = pageH - 10;          // usable bottom
  // Column anchors — the landscape values are the tuned originals; portrait uses
  // a compressed set that fits the 210mm-wide page.
  const cCoursesR = landscape ? 168 : 112;
  const cCoversR = landscape ? 184 : 128;
  const cPriceR = landscape ? 206 : 146;
  const cNotesX = landscape ? 210 : 150;
  const notesW = RIGHT - cNotesX;          // notes wrap width
  const itemW = cCoursesR - M - 24;        // wrap width for the Item column
  const LH = 4;              // body line height

  // Group in the ROWS' incoming order, which is the page's sort order — the grid
  // and this print share the same sorted `displayRows`, so the sequence of
  // services / carriages / bookings follows whatever column the user sorted by
  // (default, unsorted, is the API's service → carriage → seats → booking order).
  // Items within a booking are re-ordered to course order below.

  // carriage group (service + carriage) → bookings → items
  interface Bk { acc: string; tel: string; carriage: string; seats: string; svc: string; bk: string; items: Row[] }
  interface CG { service: string; carriage: string; bookings: Bk[] }
  const groups: CG[] = [];
  const cgIndex = new Map<string, CG>();
  const bkIndex = new Map<string, Bk>();
  for (const r of rows) {
    const carriage = (r.Carriage as string) || "(no carriage)";
    const svc = [r.Day as string, prettyDate(r.JourneyDate), timeLabel(r.JourneyTime)].filter(Boolean).join(" ");
    const cgKey = `${String(r.JourneyDate ?? "")}|${String(r.JourneyTime ?? "")}|${carriage}`;
    let cg = cgIndex.get(cgKey);
    if (!cg) { cg = { service: svc, carriage, bookings: [] }; cgIndex.set(cgKey, cg); groups.push(cg); }
    const bk = r.Booking == null ? "—" : String(r.Booking);
    const bkKey = `${cgKey}|${bk}`;
    let b = bkIndex.get(bkKey);
    if (!b) {
      b = { acc: (r.AccName as string) || "(no account)", tel: (r.TelephoneNo as string) || "", carriage, seats: (r.Seats as string) || "", svc, bk, items: [] };
      bkIndex.set(bkKey, b);
      cg.bookings.push(b);
    }
    b.items.push(r);
  }

  // Items within each booking always print in course order (Seat → Starter →
  // Main → Dessert) for the kitchen, whatever column the page was sorted by.
  for (const cg of groups) {
    for (const b of cg.bookings) {
      b.items.sort((a, z) =>
        (Number(a.Seq ?? 99) - Number(z.Seq ?? 99)) || String(a.Item ?? "").localeCompare(String(z.Item ?? ""))
      );
    }
  }

  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });

  // Page header (title band + column header) — redrawn on every page.
  function drawPageHeader(cg: CG): number {
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(11, 61, 46);
    doc.text(`Daily Bookings — ${cg.service}  ·  ${cg.carriage}`, M, 12);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(90, 90, 90);
    doc.text(`${range.from} to ${range.to}${countingLabel ? `  ·  ${countingLabel}` : ""}  ·  printed ${printedOn}`, M, 16.5);
    const y = 22;
    doc.setFillColor(31, 77, 63); doc.rect(M, y - 3.6, RIGHT - M, 5, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(255, 255, 255);
    doc.text("Item", M + 1, y);
    doc.text("Courses", cCoursesR, y, { align: "right" });
    doc.text("Covers", cCoversR, y, { align: "right" });
    doc.text("Price", cPriceR, y, { align: "right" });
    doc.text("Notes", cNotesX + 1, y);
    return y + 6;
  }

  // Measured height of a booking block (header + items + subtotal), so we can
  // page-break before it and never split it.
  function bookingHeight(b: Bk): number {
    let h = 7; // header line
    for (const it of b.items) {
      const nl = cleanNote(it.Notes) ? doc.splitTextToSize(cleanNote(it.Notes), notesW).length : 1;
      const il = doc.splitTextToSize(String(it.Item ?? ""), itemW).length || 1;
      h += Math.max(nl, il) * LH;
    }
    return h + 6; // subtotal + separator
  }

  function drawBookingHeader(b: Bk, y: number): number {
    doc.setFontSize(11);
    let x = M;
    const seg = (txt: string, bold: boolean) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(txt, x, y);
      x += doc.getTextWidth(txt);
    };
    doc.setTextColor(11, 61, 46);
    seg(b.acc, true);                                   // surname — bold
    seg(`  ·  Booking ${b.bk}  ·  `, false);
    seg(b.carriage, true);                              // carriage — bold
    seg(`  ·  ${b.seats ? `Seats ${b.seats}  ·  ` : ""}${b.tel ? `Tel ${b.tel}  ·  ` : ""}${b.svc}`, false);
    return y + 6;
  }

  let started = false;
  let y = 0;
  let gCourses = 0, gCovers = 0, gPrice = 0;

  for (const cg of groups) {
    if (started) doc.addPage();
    started = true;
    y = drawPageHeader(cg);

    for (const b of cg.bookings) {
      if (y + bookingHeight(b) > BOTTOM && y > 30) { doc.addPage(); y = drawPageHeader(cg); }
      y = drawBookingHeader(b, y);

      let sc = 0, sv = 0, sp = 0;
      for (const it of b.items) {
        const courses = Number(it.Courses ?? 0) || 0;
        const covers = Number(it.Covers ?? 0) || 0;
        const price = Number(it.Price ?? 0) || 0;
        sc += courses; sv += covers; sp += price;
        const note = cleanNote(it.Notes);
        const noteLines = note ? doc.splitTextToSize(note, notesW) : [];
        const itemLines = doc.splitTextToSize(String(it.Item ?? ""), itemW);
        const lines = Math.max(noteLines.length, itemLines.length, 1);
        // "Overs and unders" lines are drawn as a solid RED BLOCK with white
        // bold text so the kitchen can't miss them.
        const os = isOverShort(it);
        if (os) {
          doc.setFillColor(198, 40, 40);
          doc.rect(M, y - 3.3, RIGHT - M, lines * LH, "F");
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setFont("helvetica", "normal");
          doc.setTextColor(35, 35, 35);
        }
        doc.setFontSize(8);
        doc.text(itemLines, M + 1, y);
        if (courses) doc.text(String(courses), cCoursesR, y, { align: "right" });
        if (covers) doc.text(String(covers), cCoversR, y, { align: "right" });
        if (price) doc.text(money(price), cPriceR, y, { align: "right" });
        if (noteLines.length) doc.text(noteLines, cNotesX + 1, y);
        y += lines * LH;
      }
      gCourses += sc; gCovers += sv; gPrice += sp;

      // per-booking subtotal
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(90, 90, 90);
      doc.text("Booking subtotal", M + 1, y);
      if (sc) doc.text(String(sc), cCoursesR, y, { align: "right" });
      if (sv) doc.text(String(sv), cCoversR, y, { align: "right" });
      doc.text(money(sp), cPriceR, y, { align: "right" });
      y += 2.5;
      doc.setDrawColor(219, 210, 196); doc.line(M, y, RIGHT, y);
      y += 4;
    }
  }

  // Report totals (all carriages) at the end — new page if no room.
  if (started) {
    if (y + 8 > BOTTOM) { doc.addPage(); y = 20; }
    doc.setFillColor(31, 77, 63); doc.rect(M, y - 3.6, RIGHT - M, 6, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(255, 255, 255);
    doc.text("Report totals (all carriages)", M + 1, y);
    doc.text(String(gCourses), cCoursesR, y, { align: "right" });
    doc.text(String(gCovers), cCoversR, y, { align: "right" });
    doc.text(money(gPrice), cPriceR, y, { align: "right" });
  }

  doc.save(`bookings-detail-grouped-${range.from}_to_${range.to}.pdf`);
}
